import React from 'react';
import styled from '@emotion/styled';

import { colors } from '../../../design-system';
import { getColorImageLink } from '../../product/utils';
import { Color as ColorType } from '../../product/types';
import { ToolTip } from '../../common/components/Custom';
import { getColorBorder } from '../utils';

type Props = {
  productRef: string;
  isSelected: boolean;
  reverseToolTip?: boolean;
  leftAlignToolTip?: boolean;
  color: ColorType;
  handleColorClick?: (color: ColorType) => void;
};

const StyledButton = styled.button<{ border: string; item: ColorType; isSelected: boolean }>`
  padding: 0;
  border-radius: 50%;
  cursor: ${({ isSelected }) => (isSelected ? 'default' : 'pointer')};
  border: ${(props) => props.border};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -12px;
    bottom: -12px;
    left: -12px;
    right: -12px;
    background: transparent;
  }

  @media (hover: hover) {
    :hover {
      .color-tool-tip {
        border: ${({ item, isSelected }) =>
          !isSelected
            ? `3px solid ${item.colorLabel === 'Blanc' ? colors.LIGHT : colors.WHITE}`
            : `2px solid ${colors.WHITE}`};
        box-shadow: ${({ isSelected }) =>
          !isSelected ? `0px 0px 0px 1px ${colors.BLACK}` : 'none'};
      }
    }
  }

  .color-tool-tip-message {
    margin: 0;
    text-transform: uppercase;
    font-size: 1.2rem;
    line-height: 1.6rem;
    color: ${colors.MAIN_GREY};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

export const Color = ({
  productRef,
  isSelected,
  reverseToolTip = false,
  leftAlignToolTip = false,
  color,
  handleColorClick = () => null,
}: Props) => {
  const { colorRef, realColorRef, detailedColorLabel } = color;
  const srcIcon = getColorImageLink(realColorRef || colorRef);

  const isWhitish =
    ['Blanc', 'Ivoire'].includes(detailedColorLabel) ||
    ['A1', 'AN', 'AW', 'CM', 'I0', 'I1', 'I2', 'I3', 'I4', 'I6', 'I8', 'IV', 'MK'].includes(
      colorRef
    );

  const objectID = `${productRef}/${colorRef}`;

  return (
    <StyledButton
      id={`product-card-item-color-${objectID}-button`}
      data-testid={`product-card-item-color-${objectID}-button`}
      onClick={() => handleColorClick(color)}
      border={getColorBorder({ isSelected, isWhitish })}
      item={color}
      isSelected={isSelected}
      aria-label={`Bouton pour sélectionner la couleur ${detailedColorLabel} du produit`}
    >
      <ToolTip
        className="color-tool-tip"
        background={`url(${srcIcon})`}
        backgroundPosition="center"
        backgroundSize="cover"
        border={isSelected ? `2px solid ${colors.WHITE}` : 'none'}
        borderRadius="50%"
        size="20px"
        reverse={reverseToolTip}
        isLeftAligned={leftAlignToolTip}
      >
        <div>
          <p className="color-tool-tip-message">{detailedColorLabel}</p>
        </div>
      </ToolTip>
      <span className="sr-only">{detailedColorLabel}</span>
    </StyledButton>
  );
};
